import React, { useState, useEffect, useMemo } from "react"
import Input from "aldoo-ra/Input"
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline"

export default function FieldQuickSearch({
  onSearchChange,
  onFilteredFieldsChange, // New prop to pass filtered fields back to parent
  contentType,
  localFieldValues = {},
  variant = "default",
  className = "",
  placeholder = "Search in this content...",
  disabled = false
}) {
  const [searchTerm, setSearchTerm] = useState("")

  // Deep search function to recursively traverse objects and arrays
  const deepSearchObject = (obj, searchTerm) => {
    if (!obj || typeof obj !== 'object') return false

    const term = searchTerm.toLowerCase()

    // If it's an array, search each item
    if (Array.isArray(obj)) {
      for (const item of obj) {
        if (typeof item === 'string' && item.toLowerCase().includes(term)) {
          return true
        }
        if (typeof item === 'number' && item.toString().includes(term)) {
          return true
        }
        if (typeof item === 'object' && deepSearchObject(item, searchTerm)) {
          return true
        }
      }
      return false
    }

    // For objects, search all properties
    for (const [key, value] of Object.entries(obj)) {
      // Search in the property key itself
      if (key.toLowerCase().includes(term)) {
        return true
      }

      // Search in string values
      if (typeof value === 'string' && value.toLowerCase().includes(term)) {
        return true
      }

      // Search in number values
      if (typeof value === 'number' && value.toString().includes(term)) {
        return true
      }

      // Search in boolean values
      if (typeof value === 'boolean' && value.toString().toLowerCase().includes(term)) {
        return true
      }

      // Recursively search nested objects and arrays
      if (typeof value === 'object' && value !== null && deepSearchObject(value, searchTerm)) {
        return true
      }
    }

    return false
  }

  // Enhanced search filtering function
  const matchesSearch = (field, searchTerm, fieldValues) => {
    if (!searchTerm) return true

    const term = searchTerm.toLowerCase()

    // Search in field definition properties
    if (field.id?.toLowerCase().includes(term)) return true
    if (field.label?.toLowerCase().includes(term)) return true
    if (field.description?.toLowerCase().includes(term)) return true
    if (field.type?.toLowerCase().includes(term)) return true
    if (field.placeholder?.toLowerCase().includes(term)) return true

    // Get current field value
    const fieldValue = fieldValues[field.id]

    if (!fieldValue) return false


    // Search in simple string values
    if (typeof fieldValue === 'string' && fieldValue.toLowerCase().includes(term)) {
      return true
    }

    // Search in number values converted to string
    if (typeof fieldValue === 'number' && fieldValue.toString().includes(term)) {
      return true
    }

    // Search in boolean values converted to string
    if (typeof fieldValue === 'boolean' && fieldValue.toString().toLowerCase().includes(term)) {
      return true
    }

    // Deep search in objects and arrays (handles TextBundle, nested objects, etc.)
    if (typeof fieldValue === 'object' && fieldValue !== null) {
      return deepSearchObject(fieldValue, searchTerm)
    }

    return false
  }

  // Get filtered fields based on search term with CSV support
  const filteredFields = useMemo(() => {
    if (!searchTerm || !contentType?.fields) return []

    // Split by comma for CSV-style search (OR logic)
    const searchTerms = searchTerm.split(',').map(term => term.trim()).filter(term => term.length > 0)

    // If no valid terms after splitting, return empty
    if (searchTerms.length === 0) return []

    // Filter fields that match ANY of the search terms (OR logic)
    return contentType.fields.filter(field =>
      searchTerms.some(term => matchesSearch(field, term, localFieldValues))
    )
  }, [searchTerm, contentType?.fields, localFieldValues])

  // Notify parent component about search state and filtered results
  useEffect(() => {
    const searchData = {
      searchTerm,
      filteredFields,
      hasActiveSearch: searchTerm && searchTerm.trim().length > 0
    }

    // Call the search change callback
    if (onSearchChange) {
      onSearchChange(searchTerm)
    }

    // Call the filtered fields callback
    if (onFilteredFieldsChange) {
      onFilteredFieldsChange(searchData)
    }
  }, [searchTerm, filteredFields, onSearchChange, onFilteredFieldsChange])

  const handleSearchChange = (e) => {
    const value = e.target.value
    setSearchTerm(value)
  }

  const handleClear = () => {
    setSearchTerm("")
  }

  const inputVariant = variant.includes('admin') ? 'admin' : 'default'

  return (
    <div className={className}>
      <Input
        variant={inputVariant}
        value={searchTerm}
        onChange={handleSearchChange}
        placeholder={placeholder}
        disabled={disabled}
        className="w-full"
        icon={
          <MagnifyingGlassIcon className="h-5 w-5 text-admin_text_secondary" />
        }
        showClearButton={searchTerm.length > 0}
        onClear={handleClear}
      />
    </div>
  )
}